import React, { FC, useCallback, useEffect, useState } from "react";

import {
    Button,
    GoogleBusinessProfile,
    LoadingSpinner,
    OnboardingAside,
    OnboardingContent,
    Place,
    PlaceList,
    PrimaryAlternateControl,
} from "@xapp/components";

import { isUnsupportedBusinessType, getUnsupportedBusinessTypeReason } from "../../utils/isUnsupportedBusinessType";

// import { OnboardingProps } from "../../models";

export interface FindAndConfirmProfileStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Whether the business is loading
     */
    loading: boolean;
    /**
     * The place to confirm or find a profile for
     */
    place?: Place;
    /**
     * What was searched to find the results
     */
    searchTerms?: string;
    /**
     * Whether a place is required to continue
     */
    requirePlace?: boolean;
    /**
     * Results for the user to select a place from
     */
    results?: Place[];
    /**
     * Will get more details about the place
     *
     */
    onSelect?: (place: Place) => void;
    /**
     * Called when the user confirms a place
     *
     * @param place
     * @returns
     */
    onNext: (place?: Place) => void;

    onBack: () => void;
}

export const FindAndConfirmProfileStep: FC<FindAndConfirmProfileStepProps> = (props) => {
    const { aside, loading, requirePlace, place, searchTerms, results, onNext, onBack, onSelect } = props;

    const [placeToConfirm, setPlaceToConfirm] = useState<Place>(place);

    const [showPlaceRequired, setShowPlaceRequired] = useState<boolean>(false);
    const [showUnsupportedType, setShowUnsupportedType] = useState<boolean>(false);

    useEffect(() => {
        // if a place now all of a sudden has a maps URL and the previous does not
        if ((place && !placeToConfirm) || placeToConfirm?.mapsUrl !== place?.mapsUrl) {
            setPlaceToConfirm(place);
        }
        
        // Check if the place has an unsupported business type
        if (place && isUnsupportedBusinessType(place)) {
            setShowUnsupportedType(true);
        } else {
            setShowUnsupportedType(false);
        }
    }, [place, placeToConfirm]);

    // console.log("FindAndConfirmProfileStep", props);
    // console.log("FindAndConfirmProfileStep place", place);
    // console.log("FindAndConfirmProfileStep placeToConfirm", placeToConfirm);

    const handlePrimary = useCallback(() => {
        if (place) {
            onNext(place);
        }
    }, [onNext, place]);

    const handleSelect = useCallback(
        (place: Place) => {
            if (typeof onSelect === "function") {
                onSelect(place);
            }
            setPlaceToConfirm(place);
        },
        [onSelect]
    );

    const handleNoPlace = useCallback(() => {
        // show a message stating that we cannot continue without a place
        // provide them with instructions on how to create a profile
        // which they cannot skip
        if (requirePlace) {
            // show require place page
            setShowPlaceRequired(true);
            return;
        }

        if (typeof onSelect === "function") {
            onNext(undefined);
        }
    }, [onNext, onSelect, requirePlace]);

    const handleShowResults = useCallback(() => {
        if (typeof onSelect === "function") {
            onSelect(undefined);
        }
        setPlaceToConfirm(undefined);
    }, [onSelect]);

    const showAlternate = results && results.length > 1;

    const getContent = useCallback(() => {
        if (showUnsupportedType && placeToConfirm) {
            const reason = getUnsupportedBusinessTypeReason(placeToConfirm);
            return (
                <>
                    <div style={{ height: "400px", width: "100%", overflowY: "auto" }}>
                        <div
                            style={{
                                display: "flex",
                                alignContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                gap: "10px",
                                paddingTop: "10px",
                            }}
                        >
                            <h2>Business Type Not Supported</h2>
                            <p style={{ textAlign: "center", maxWidth: "400px" }}>
                                We're sorry, but Book Online is currently only available for home service businesses. 
                                {reason} isn't supported at this time.
                            </p>
                        </div>
                        <PrimaryAlternateControl
                            labels={{
                                primary: "Search for a Different Business",
                            }}
                            onPrimary={() => {
                                setShowUnsupportedType(false);
                                setPlaceToConfirm(undefined);
                                onBack();
                            }}
                        />
                    </div>
                </>
            );
        } else if (showPlaceRequired) {
            return (
                <>
                    <div style={{ height: "400px", width: "100%", overflowY: "auto" }}>
                        <div
                            style={{
                                display: "flex",
                                alignContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                gap: "10px",
                                paddingTop: "10px",
                            }}
                        >
                            <h2>You need a Google Business Profile to continue</h2>
                            <b>Don't have a profile? No problem!</b>
                            <p>
                                It is free and easy to do and it will help you grow your business. Please follow these
                                instructions on how to create your profile with Google:
                            </p>
                            <a
                                href="https://xapp.ai/a-simple-guide-to-setting-up-and-verifying-your-google-business-profile/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to Create a Google Business Profile
                            </a>
                            <p>
                                Come back after your profile is live and a Book Online button can be added in 48 hours.
                            </p>
                        </div>
                        <PrimaryAlternateControl
                            labels={{
                                primary: "Search Again",
                            }}
                            onPrimary={() => {
                                setShowPlaceRequired(false);
                                onBack();
                            }}
                        />
                    </div>
                </>
            );
        } else {
            if (placeToConfirm) {
                return (
                    <>
                        {searchTerms && (
                            <>
                                <small>We searched using...</small>
                                <i>{searchTerms}</i>
                            </>
                        )}

                        <GoogleBusinessProfile
                            loading={loading || !placeToConfirm}
                            place={placeToConfirm}
                            showContact={true}
                        />

                        <h3>Is this your business?</h3>

                        <PrimaryAlternateControl
                            labels={{
                                primary: "Yes",
                                alternate: showAlternate ? "No, show the results" : "No",
                            }}
                            onPrimary={handlePrimary}
                            onAlternate={showAlternate ? handleShowResults : handleNoPlace}
                        />
                        <Button
                            style={{ marginTop: "10px", border: "none", textDecoration: "underline" }}
                            id="noProfileButton"
                            variation="secondary"
                            onClick={handleNoPlace}
                            label={"I don't have a profile"}
                        />
                    </>
                );
            } else {
                return (
                    <>
                        {searchTerms && (
                            <>
                                <small>We searched using...</small>
                                <i>{searchTerms}</i>
                            </>
                        )}

                        <div style={{ height: "300px", width: "100%", overflowY: "auto", padding: "15px" }}>
                            <PlaceList places={results} onSelect={handleSelect} />
                        </div>
                        <h3>Is one of the above your business?</h3>

                        <p>Select your business above or if you don't see it, we can move on to the next step.</p>

                        <PrimaryAlternateControl
                            labels={{
                                primary: "No, I don't see it",
                                alternate: "Back",
                            }}
                            onPrimary={handleNoPlace}
                            onAlternate={onBack}
                        />

                        <Button
                            style={{ marginTop: "10px", border: "none", textDecoration: "underline" }}
                            id="noProfileButton"
                            variation="secondary"
                            onClick={handleNoPlace}
                            label={"I don't have a profile"}
                        />
                    </>
                );
            }
        }
    }, [
        handleNoPlace,
        handlePrimary,
        handleSelect,
        handleShowResults,
        loading,
        onBack,
        placeToConfirm,
        results,
        searchTerms,
        showAlternate,
        showPlaceRequired,
        showUnsupportedType,
    ]);

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {loading ? <LoadingSpinner loadingText="Loading place details..." /> : getContent()}
            </OnboardingContent>
        </>
    );
};
